import { useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';

/**
 * Hook to select a value from a list of values and store it in local storage
 * @param key the key to store the value in local storage
 * @param values the values to select from
 * @returns the current value, the current option, the options and the function to change the value
 */
export function useLocalStorageSelect(key: string, values: string[]) {
    // get instanceID from local storage
    const [current, setCurrent] = useLocalStorage(key, values?.[0]);

    // get available instances for specific client
    const options = values?.map((cl) => ({ value: cl, label: cl }));

    // store instanceID in local storage et change select option
    const onChange = (option: any) => {
        if (option) {
            setCurrent(option.value);
        }
    };

    useEffect(() => {
        let storedValue: string | null = null;
        try {
            storedValue = localStorage.getItem(key);
        } catch {
            // localStorage unavailable (e.g. Safari private mode)
        }

        if (
            (current && values.length > 0 && !values.includes(current)) || // if value is not in the list, select it to the first value in the list
            (!storedValue && values?.length > 0) // if instanceID is not set, set it to the first instanceID
        ) {
            setCurrent(values[0]);
        }
    }, [current, values]);

    return {
        currentValue: current,
        currentOption: current ? { value: current, label: current } : undefined,
        options,
        onChange,
    };
}
